import api from './api'
import { MovimentacaoCaixa, CaixaResumo, expenseAffectsStoreCash, movimentacaoAfetaSaldoCaixa } from '../types'
import saleService from './saleService'
import expenseService from './expenseService'
import vehicleService from './vehicleService'
import type { Sale } from '../types'
import { getCashRecognizedProfitFromSale } from '../utils/saleRealProfit'

/** Converte valor para número (evita concatenação de strings nos cálculos). */
const toNum = (v: unknown): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  const s = String(v).trim()
  if (s === '') return 0
  if (/,\d*$/.test(s)) return Number(s.replace(/\./g, '').replace(',', '.')) || 0
  return parseFloat(s) || 0
}

type ApiCashMovement = MovimentacaoCaixa & {
  due_date?: string
  payable_status?: string
  payable_note?: string
}

/** Normaliza campos de conta a pagar vindos da API (camelCase ou snake_case). */
const normalizeCashMovement = (raw: Record<string, unknown>): MovimentacaoCaixa => {
  const r = raw as unknown as ApiCashMovement
  return {
    ...r,
    id: String(raw.id ?? ''),
    dueDate: r.dueDate ?? r.due_date,
    payableStatus:
      (r.payableStatus as MovimentacaoCaixa['payableStatus']) ??
      (r.payable_status === 'pending' || r.payable_status === 'paid' ? r.payable_status : undefined),
    payableNote: r.payableNote ?? r.payable_note,
  }
}

export const cashMovementService = {
  getAll: async (): Promise<MovimentacaoCaixa[]> => {
    try {
      const response = await api.get('/cash-movements')
      const list = response.data || []
      return Array.isArray(list) ? list.map((item: Record<string, unknown>) => normalizeCashMovement(item)) : []
    } catch (error) {
      console.error('Erro ao buscar movimentações:', error)
      throw error
    }
  },

  get: async (id: string | number): Promise<MovimentacaoCaixa> => {
    try {
      const response = await api.get(`/cash-movements/${id}`)
      return normalizeCashMovement(response.data as Record<string, unknown>)
    } catch (error) {
      console.error(`Erro ao buscar movimentação ${id}:`, error)
      throw error
    }
  },

  create: async (data: Omit<MovimentacaoCaixa, 'id' | 'createdAt'>): Promise<MovimentacaoCaixa> => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[cash-movements] POST /cash-movements — corpo enviado:', data)
        console.log('[cash-movements] JSON:', JSON.stringify(data, null, 2))
      }
      const response = await api.post('/cash-movements', data)
      return normalizeCashMovement(response.data as Record<string, unknown>)
    } catch (error) {
      console.error('Erro ao criar movimentação:', error)
      throw error
    }
  },

  update: async (id: string | number, data: Partial<MovimentacaoCaixa>): Promise<MovimentacaoCaixa> => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[cash-movements] PUT /cash-movements/${id} — corpo enviado:`, data)
        console.log('[cash-movements] JSON:', JSON.stringify(data, null, 2))
      }
      const response = await api.put(`/cash-movements/${id}`, data)
      return normalizeCashMovement(response.data as Record<string, unknown>)
    } catch (error) {
      console.error(`Erro ao atualizar movimentação ${id}:`, error)
      throw error
    }
  },

  delete: async (id: string | number): Promise<number> => {
    try {
      const response = await api.delete(`/cash-movements/${id}`)
      return response.status
    } catch (error) {
      console.error(`Erro ao excluir movimentação ${id}:`, error)
      throw error
    }
  },

  getResumo: async (): Promise<CaixaResumo> => {
    const movimentacoes = await cashMovementService.getAll()

    let totalVendas = 0
    let receitaRealVendas = 0
    let totalDespesas = 0

    try {
      const [sales, expenses] = await Promise.all([
        saleService.getAllSales(),
        expenseService.getAllExpenses(),
      ])

      totalVendas = (sales || []).reduce((sum, s) => sum + toNum(s.sale_price), 0)
      // Enriquecer vendas com veículo (despesas / compra) para lucro real
      let salesWithVehicle = sales || []
      const missingVehicle = salesWithVehicle.some((s: any) => s.vehicle_id && !s.vehicle)
      if (missingVehicle) {
        try {
          const vehicles = await vehicleService.getAllVehicles()
          const byId: Record<number, any> = {}
          ;(vehicles || []).forEach((v: any) => {
            byId[v.id] = v
          })
          salesWithVehicle = salesWithVehicle.map((s: any) =>
            s.vehicle ? s : { ...s, vehicle: byId[s.vehicle_id] ?? null }
          )
        } catch (_) {}
      }

      // Entrada do caixa pelas vendas = lucro real proporcional ao recebido (a prazo: conforme baixas)
      const salesForCash = await saleService.enrichTermSalesWithInstallments(salesWithVehicle)
      receitaRealVendas = salesForCash.reduce(
        (sum: number, s: Sale) => sum + getCashRecognizedProfitFromSale(s),
        0
      )

      const soldVehicleIds = new Set(
        (salesWithVehicle || [])
          .map((s: any) => s.vehicle_id)
          .filter((id: unknown): id is number => typeof id === 'number' && id > 0)
      )

      // Despesas de veículos já vendidos já entram no lucro real acima; não subtrair de novo
      totalDespesas = (expenses || []).reduce((sum, e) => {
        if (!expenseAffectsStoreCash(e)) return sum
        if (soldVehicleIds.has(e.vehicle_id)) return sum
        return sum + toNum(e.amount)
      }, 0)
    } catch (e) {
      console.error('Erro ao carregar vendas/despesas:', e)
    }

    const totalMovimentacoesEntrada = movimentacoes
      .filter(m => m.type === 'entrada')
      .reduce((sum, m) => sum + toNum(m.amount), 0)

    const totalMovimentacoesSaida = movimentacoes
      .filter(m => m.type === 'saida' && movimentacaoAfetaSaldoCaixa(m))
      .reduce((sum, m) => sum + toNum(m.amount), 0)

    const totalEntradas = receitaRealVendas + totalMovimentacoesEntrada
    const totalSaidas = totalDespesas + totalMovimentacoesSaida
    const lucroCaixa = totalEntradas - totalSaidas

    return {
      totalVendas,
      receitaRealVendas,
      totalMovimentacoesEntrada,
      totalMovimentacoesSaida,
      totalDespesas,
      totalEntradas,
      totalSaidas,
      lucroCaixa,
      movimentacoes: [...movimentacoes].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      ),
    }
  }
}

export default cashMovementService
